import { isPlainObject } from '@vestigas/utils';

const hasValue = (value: unknown): boolean =>
  !['', undefined, null].includes(value);

/**
 * Checks if a value or any of its nested elements has a non-empty value.
 * A value is considered empty if it's an empty string, undefined, or null.
 *
 * @param value - The value to check. Can be a primitive, array, or object.
 * @returns {boolean} True if the value or any of its nested elements has a non-empty value, false otherwise.
 *
 * @example
 * hasSomeValue('') // false
 * hasSomeValue(['', null, undefined]) // false
 * hasSomeValue(['', 'hello', null]) // true
 * hasSomeValue({ a: '', b: 'hello' }) // true
 */
export const hasSomeValue = (value: unknown): boolean => {
  if (!hasValue(value)) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasSomeValue(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some((item) => hasSomeValue(item));
  }

  return hasValue(value);
};

/**
 * Replaces all undefined values in an object with empty strings.
 *
 * @param obj - The object to replace undefined values in.
 * @returns The object with all undefined values replaced with empty strings.
 */
export const replaceUndefinedWithEmptyString = (
  object: Record<string, unknown> | unknown[],
): Record<string, unknown> | unknown[] | string | undefined => {
  if (object === null) {
    return null;
  }

  if (object === undefined) {
    return '';
  }

  if (typeof object !== 'object') {
    return object;
  }

  if (Array.isArray(object)) {
    return object.map((item) => replaceUndefinedWithEmptyString(item));
  }

  return Object.keys(object).reduce<Record<string, unknown>>(
    (accumulator, key) => {
      accumulator[key] = replaceUndefinedWithEmptyString(object[key]);
      return accumulator;
    },
    {},
  );
};
